import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { ShieldCheck, HeartHandshake } from "lucide-react";

export const Route = createFileRoute("/_authenticated/parent-consent")({
  component: ConsentPage,
});

function ConsentPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [agree, setAgree] = useState(false);
  const [age, setAge] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!agree || !age || !user) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        // Marks the parent as having reviewed consent.
        onboarded: true,
      })
      .eq("id", user.id);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Thank you 💛");
    nav({ to: "/app" });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 px-6 py-10">
      <div className="mx-auto max-w-xl rounded-3xl border border-border bg-card p-8 shadow-xl">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-primary/10 text-primary">
          <ShieldCheck className="h-7 w-7" />
        </div>
        <h1 className="mt-4 text-center text-2xl font-bold">Parental Consent</h1>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          Jozori is built for families. We need your consent to create profiles
          for your children and process their learning progress.
        </p>

        <div className="mt-6 space-y-3 rounded-2xl bg-muted/40 p-4 text-sm">
          <p className="flex gap-2"><HeartHandshake className="h-4 w-4 mt-0.5 text-primary" /> We never sell or share children's data.</p>
          <p className="flex gap-2"><HeartHandshake className="h-4 w-4 mt-0.5 text-primary" /> No third-party ads are shown to children.</p>
          <p className="flex gap-2"><HeartHandshake className="h-4 w-4 mt-0.5 text-primary" /> You can export or delete your family's data anytime.</p>
        </div>

        <label className="mt-6 flex items-start gap-3 text-sm">
          <input type="checkbox" checked={age} onChange={(e) => setAge(e.target.checked)} className="mt-1 h-4 w-4" />
          <span>I confirm I am the parent or legal guardian, and I am at least 18 years old.</span>
        </label>
        <label className="mt-3 flex items-start gap-3 text-sm">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 h-4 w-4" />
          <span>
            I have read the <Link to="/privacy" className="underline">Privacy Policy</Link> and
            <Link to="/terms" className="underline"> Terms of Service</Link>, and I consent
            to Jozori collecting limited learning data for my children to operate the service.
          </span>
        </label>

        <button
          disabled={!agree || !age || saving}
          onClick={submit}
          className="mt-6 w-full rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition disabled:opacity-50"
        >
          {saving ? "Saving…" : "Give consent & continue"}
        </button>
      </div>
    </div>
  );
}
